import React, { useState } from 'react';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Mail, Phone, MapPin, Send, MessageSquare } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    alert('Thank you for contacting us! We will get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      content: "support@deyvainfotech.com",
      link: "mailto:support@deyvainfotech.com"
    },
    {
      icon: Phone,
      title: "Call Us",
      content: "+91 80564 88828",
      link: "tel:+918056488828"
    },
    {
      icon: MapPin,
      title: "Visit Us",
      content: "Chennai, India",
      link: "#"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-4xl mx-auto text-center"
          >
            <MessageSquare className="w-16 h-16 mx-auto mb-6" style={{ color: '#FF6B9D' }} />
            <h1 
              className="text-5xl md:text-6xl font-bold mb-6"
              style={{ color: '#0A0A0A' }}
            >
              Get in Touch
            </h1>
            <p className="text-xl text-gray-600">
              Have a question or feedback? We'd love to hear from you!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-20">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <motion.a
                  key={index}
                  href={info.link}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-all text-center"
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 140, 66, 0.1) 0%, rgba(255, 107, 157, 0.1) 100%)'
                    }}
                  >
                    <Icon className="w-8 h-8" style={{ color: '#FF6B9D' }} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2" style={{ color: '#0A0A0A' }}>
                    {info.title}
                  </h3>
                  <p className="text-gray-600">
                    {info.content}
                  </p>
                </motion.a>
              );
            })}
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 md:p-12 border border-gray-200 shadow-lg"
            >
              <h2 className="text-3xl font-bold mb-2 text-center" style={{ color: '#0A0A0A' }}>
                Send Us a Message
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Fill out the form below and we'll get back to you within 24 hours
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-pink-400 focus:outline-none"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-pink-400 focus:outline-none"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-pink-400 focus:outline-none"
                    placeholder="How can we help you?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-pink-400 focus:outline-none resize-none"
                    placeholder="Tell us more about your question or feedback..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full md:w-auto inline-flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '26px',
                    padding: '16px 48px',
                    fontSize: '16px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  <Send className="w-5 h-5" />
                  Send Message
                </button>
              </form>
            </motion.div>
          </div>

          {/* Response Time Notice */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mt-12 text-center"
          >
            <p className="text-sm text-gray-600">
              ⏱️ Average response time: <strong>12-24 hours</strong> • Available Monday-Saturday, 9 AM - 6 PM IST
            </p>
          </motion.div>
        </div>
      </section>

      {/* Business Inquiries Section */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Business & Partnership Inquiries
            </h2>
            <p className="text-gray-600 mb-6">
              Interested in partnering with Typeit 4U or have a business proposal?
            </p>
            <a
              href="mailto:business@deyvainfotech.com"
              className="inline-flex items-center gap-2 text-lg font-medium"
              style={{ color: '#FF6B9D' }}
            >
              business@deyvainfotech.com
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
